import { Mission } from "game/quests/types";

// Game Statistics
export interface GameStats {
  difficulty: string;
  moves: number;
  time: number | string; // в секундах или строка для отображения
  showDifficulty?: boolean;
  leftX: number;
  rightX: number;
}

export type DifficultyLevel = 'Easy' | 'Medium' | 'Hard';

// UI State
export interface UIState {
  gameStats: GameStats;
  modals: ModalsState;
  settings: Checkboxes[];
  design: DesignState;
  indicators: IndicatorsState;
  buttons: ButtonsState;
  labels: LabelsState;
  isExpanded: boolean;
  tutorialActive: boolean;
  isInitialized: boolean;
  missions: {
    dailyMission?: Mission;
    isNewMissionCompleted?: boolean;
    isNewMissionCompletedOnMounted?: boolean;
  };
  
}

export interface Checkboxes {
  key: string;
  label: string;
  checked: boolean;
  visible: boolean;
}

// Modals State
export interface ModalsState {
  designModal: {
    isOpen: boolean;
    activeTab: DesignTab;
  };
  settingsModal: {
    isOpen: boolean;
  };
  missionsModal: {
    isOpen: boolean;
    weeklyDisabled: boolean;
  };
  missionCompletePopup: {
    placement: 'winscreen' | 'sidebar';
    isOpen: boolean;
  };
}

export type DesignTab = 'patterns' | 'cards' | 'backgrounds';

// Settings State
export interface SettingsState {
  soundEnabled: boolean;
  animationsEnabled: boolean;
  autoCompleteEnabled: boolean;
  hintsEnabled: boolean;
}

// Design State
export interface DesignState {
  selectedPattern: string;
  selectedCardTheme: string;
  selectedBackground: string;
}

// Indicators State
export interface IndicatorsState {
  shareIndicator: boolean;
  hintsIndicator: boolean;
  hintsCount: number;
  settingsIndicator: boolean;
}

// Buttons State
export interface ButtonsState {
  undoDisabled: boolean;
  hintsDisabled: boolean;
  magicWandDisabled: boolean;
  showMagicWand: boolean;
  showAutocomplete: boolean;
  hideDesignFirstTab: boolean;
  magicWandsCount: number;
}

// Labels State  
export interface LabelsState {
  movesLabel: string;
  timeLabel: string;
  settingsText: string;
  feedbackText: string;
  designText: string;
  autocompleteText: string;
}

// Action Types
export enum UIActionTypes {
  // Universal action for updating any part of UI state
  UPDATE_UI_STATE = 'UPDATE_UI_STATE',
  TOGGLE_IS_NEW_MISSION = 'TOGGLE_IS_NEW_MISSION',
  
  // Modals
  OPEN_DESIGN_MODAL = 'OPEN_DESIGN_MODAL',
  CLOSE_DESIGN_MODAL = 'CLOSE_DESIGN_MODAL',
  SET_DESIGN_TAB = 'SET_DESIGN_TAB',
  SET_MISSIONS_TAB = 'SET_MISSIONS_TAB',
  OPEN_SETTINGS_MODAL = 'OPEN_SETTINGS_MODAL',
  CLOSE_SETTINGS_MODAL = 'CLOSE_SETTINGS_MODAL',
  CLOSE_MISSIONS_MODAL = 'CLOSE_MISSIONS_MODAL',
  CLOSE_ALL_PANELS = 'CLOSE_ALL_PANELS',
  
  // Settings
  TOGGLE_SETTING = 'TOGGLE_SETTING',
  UPDATE_SETTINGS = 'UPDATE_SETTINGS',

  OPEN_MISSION_COMPLETE_POPUP = 'OPEN_MISSION_COMPLETE_POPUP',
  CLOSE_MISSION_COMPLETE_POPUP = 'CLOSE_MISSION_COMPLETE_POPUP',
}

// Action Interfaces
export interface UpdateUIStateAction {
  type: UIActionTypes.UPDATE_UI_STATE;
  payload: Partial<UIState>;
}

export interface OpenDesignModalAction {
  type: UIActionTypes.OPEN_DESIGN_MODAL;
  payload?: DesignTab;
}

export interface CloseDesignModalAction {
  type: UIActionTypes.CLOSE_DESIGN_MODAL;
}

export interface SetDesignTabAction {
  type: UIActionTypes.SET_DESIGN_TAB;
  payload: DesignTab;
}


export interface OpenSettingsModalAction {
  type: UIActionTypes.OPEN_SETTINGS_MODAL;
}

export interface CloseSettingsModalAction {
  type: UIActionTypes.CLOSE_SETTINGS_MODAL;
}

export interface CloseMissionsModalAction {
  type: UIActionTypes.CLOSE_MISSIONS_MODAL;
}

export interface CloseAllPanelsAction {
  type: UIActionTypes.CLOSE_ALL_PANELS;
}

export interface ToggleIsNewMissionAction {
  type: UIActionTypes.TOGGLE_IS_NEW_MISSION;
  payload: boolean;
}

export interface ToggleSettingAction {
  type: UIActionTypes.TOGGLE_SETTING;
  payload: string;
}

export interface UpdateSettingsAction {
  type: UIActionTypes.UPDATE_SETTINGS;
  payload: Checkboxes[];
}

export interface OpenMissionCompletePopupAction {
  type: UIActionTypes.OPEN_MISSION_COMPLETE_POPUP;
  payload: 'winscreen' | 'sidebar';
}

export interface CloseMissionCompletePopupAction {
  type: UIActionTypes.CLOSE_MISSION_COMPLETE_POPUP;
}

export type UIAction = 
  | UpdateUIStateAction
  | OpenDesignModalAction
  | CloseDesignModalAction
  | SetDesignTabAction
  | OpenSettingsModalAction
  | CloseSettingsModalAction
  | CloseMissionsModalAction
  | CloseAllPanelsAction
  | ToggleSettingAction
  | UpdateSettingsAction
  | OpenMissionCompletePopupAction
  | CloseMissionCompletePopupAction
  | ToggleIsNewMissionAction;